import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  CheckCheck,
  MessageSquare,
  UserPlus,
  Sparkles,
  CalendarCheck2,
  Trophy,
  FileText,
  Heart,
  AlertTriangle,
  LinkIcon,
  DollarSign,
  CreditCard,
  Inbox as InboxIcon,
  Loader2,
  ClipboardCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import type { Notification } from './types'
import {
  useNotifications,
  useUnreadCount,
  useMarkAllRead,
  useMarkRead,
} from './use-notifications'

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffSec = Math.max(0, Math.round((now - then) / 1000))
  if (diffSec < 45) return 'just now'
  if (diffSec < 90) return '1m ago'
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(iso).toLocaleDateString()
}

type IconConfig = {
  readonly Icon: React.ComponentType<{ className?: string }>
  readonly accent: string
}

const ICON_BY_TYPE: Record<string, IconConfig> = {
  lead_replied: { Icon: MessageSquare, accent: 'text-emerald-300' },
  lead_connected: { Icon: UserPlus, accent: 'text-[var(--color-cobalt-bright)]' },
  lead_meeting_booked: { Icon: CalendarCheck2, accent: 'text-emerald-300' },
  lead_qualified: { Icon: Sparkles, accent: 'text-emerald-300' },
  post_engagement: { Icon: Heart, accent: 'text-[var(--color-cobalt-bright)]' },
  post_published: { Icon: FileText, accent: 'text-emerald-300' },
  post_scheduled: { Icon: FileText, accent: 'text-[var(--color-silver)]' },
  campaign_completed: { Icon: Trophy, accent: 'text-emerald-300' },
  warmup_complete: { Icon: Sparkles, accent: 'text-emerald-300' },
  escalation: { Icon: AlertTriangle, accent: 'text-red-300' },
  approval_needed: { Icon: ClipboardCheck, accent: 'text-[var(--color-cobalt-bright)]' },
  linkedin_disconnected: { Icon: LinkIcon, accent: 'text-amber-300' },
  payment_succeeded: { Icon: DollarSign, accent: 'text-emerald-300' },
  payment_failed: { Icon: CreditCard, accent: 'text-red-300' },
}

function getIcon(type: string): IconConfig {
  return ICON_BY_TYPE[type] ?? { Icon: Bell, accent: 'text-[var(--color-silver)]' }
}

type NotificationRowProps = {
  readonly notification: Notification
  readonly onNavigate: (n: Notification) => void
}

function NotificationRow({ notification, onNavigate }: NotificationRowProps) {
  const { Icon, accent } = getIcon(notification.type)
  const unread = !notification.read_at

  return (
    <button
      type="button"
      onClick={() => onNavigate(notification)}
      className={cn(
        'group relative flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
        'hover:bg-[rgba(255,255,255,0.03)]',
        unread && 'bg-[rgba(28,63,255,0.04)]',
      )}
    >
      {unread && (
        <span
          aria-hidden="true"
          className="absolute left-1.5 top-5 h-1.5 w-1.5 rounded-full bg-[var(--color-cobalt-bright)] shadow-[0_0_6px_rgba(28,63,255,0.6)]"
        />
      )}
      <div
        className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgba(255,255,255,0.04)]',
          accent,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p
          className={cn(
            'text-sm leading-snug',
            unread
              ? 'font-semibold text-[var(--color-silver-bright)]'
              : 'text-[var(--color-silver-bright)]',
          )}
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="line-clamp-2 text-xs leading-snug text-[var(--color-silver)]">
            {notification.body}
          </p>
        )}
        <p className="text-[10px] uppercase tracking-wide text-[var(--color-silver)]">
          {formatRelative(notification.created_at)}
        </p>
      </div>
    </button>
  )
}

export function NotificationsDrawer() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { data: notifications = [], isLoading } = useNotifications()
  const { data: unread = 0 } = useUnreadCount()
  const markRead = useMarkRead()
  const markAll = useMarkAllRead()

  function handleNavigate(n: Notification) {
    if (!n.read_at) markRead.mutate(n.id)
    if (n.link) {
      setOpen(false)
      // Defer navigation so the sheet's close animation isn't interrupted
      setTimeout(() => navigate(n.link!), 150)
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
          className="relative text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-cobalt)] px-1 text-[9px] font-bold leading-none text-white shadow-[0_0_8px_rgba(28,63,255,0.5)]"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[400px] max-w-full p-0 flex flex-col">
        <SheetHeader className="flex flex-row items-center justify-between border-b px-5 py-4 space-y-0">
          <div>
            <SheetTitle className="text-base">Notifications</SheetTitle>
            <p className="mt-0.5 text-xs text-[var(--color-silver)]">
              {unread > 0
                ? `${unread} unread`
                : notifications.length > 0
                  ? 'All caught up'
                  : 'Nothing yet'}
            </p>
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </SheetHeader>

        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-[var(--color-silver)]">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <InboxIcon className="h-8 w-8 text-[var(--color-silver)]/50" />
              <p className="text-sm text-[var(--color-silver-bright)]">You're all caught up</p>
              <p className="max-w-[240px] text-xs text-[var(--color-silver)]">
                Replies, connection accepts, engagements, and billing events will show up here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((n) => (
                <NotificationRow key={n.id} notification={n} onNavigate={handleNavigate} />
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
